import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface PortalProps {
  children: ReactNode;
  /** Target container; defaults to `document.body`. */
  container?: HTMLElement | null;
}

const canUseDOM = () => typeof document !== 'undefined';

/**
 * Renders children into `document.body` (or a custom container). SSR-safe: renders nothing on the server and
 * mounts synchronously on the client so anchored positioning can measure the floating element in the same commit.
 */
export function Portal({ children, container }: PortalProps) {
  const [mounted, setMounted] = useState<boolean>(canUseDOM);
  useEffect(() => {
    if (!mounted) setMounted(true);
  }, [mounted]);
  if (!mounted || !canUseDOM()) return null;
  return createPortal(children, container ?? document.body);
}
